import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  ComputerDesktopIcon,
  PhoneIcon,
  SpeakerWaveIcon,
  TvIcon,
  PresentationChartBarIcon,
  WifiIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  EyeIcon,
  PlusIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { BuildingOfficeIcon as BuildingOfficeIconSolid } from '@heroicons/react/24/solid';

const MeetingRooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFloor, setSelectedFloor] = useState('all');
  const [selectedCapacity, setSelectedCapacity] = useState('all');
  const [selectedEquipment, setSelectedEquipment] = useState('all');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [bookingDuration, setBookingDuration] = useState('60'); // minutes

  useEffect(() => {
    // Simulate fetching meeting rooms data
    const fetchRooms = () => {
      const mockRooms = [
        {
          id: 'room001',
          name: 'Conference Room A',
          description: 'Large conference room perfect for board meetings and presentations',
          floor: 3,
          building: 'Main Building',
          capacity: 12,
          equipment: ['Projector', 'Video Conferencing', 'Whiteboard', 'WiFi', 'Conference Phone'],
          amenities: ['Air Conditioning', 'Natural Light', 'Coffee Station'],
          hourly_rate: 25,
          availability: 'available',
          image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400',
          bookings: [
            { start: '09:00', end: '11:00', title: 'Board Meeting', bookedBy: 'Executive Team' },
            { start: '14:00', end: '15:30', title: 'Project Review', bookedBy: 'Dr. Sarah Johnson' }
          ],
          specifications: {
            dimensions: '6m × 8m',
            layout: 'Boardroom style',
            windows: 'Yes',
            accessibility: 'Wheelchair accessible'
          }
        },
        {
          id: 'room002',
          name: 'Conference Room B',
          description: 'Medium-sized meeting room ideal for team meetings and discussions',
          floor: 3,
          building: 'Main Building',
          capacity: 8,
          equipment: ['Projector', 'Whiteboard', 'WiFi', 'TV Display'],
          amenities: ['Air Conditioning', 'Natural Light'],
          hourly_rate: 20,
          availability: 'busy',
          image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400',
          bookings: [
            { start: '08:00', end: '10:00', title: 'Department Meeting', bookedBy: 'HR Team' },
            { start: '10:30', end: '12:00', title: 'Training Session', bookedBy: 'Training Dept' },
            { start: '13:00', end: '17:00', title: 'Workshop', bookedBy: 'Medical Staff' }
          ],
          specifications: {
            dimensions: '4m × 6m',
            layout: 'U-shape',
            windows: 'Yes',
            accessibility: 'Wheelchair accessible'
          }
        },
        {
          id: 'room003',
          name: 'Meeting Room 101',
          description: 'Cozy meeting room for small team discussions and consultations',
          floor: 1,
          building: 'Main Building',
          capacity: 6,
          equipment: ['TV Display', 'Conference Phone', 'WiFi', 'Whiteboard'],
          amenities: ['Air Conditioning', 'Coffee Machine'],
          hourly_rate: 15,
          availability: 'available',
          image: 'https://images.unsplash.com/photo-1541746972996-4e0b0f93e586?w=400',
          bookings: [
            { start: '16:00', end: '17:00', title: 'Client Call', bookedBy: 'Sales Team' }
          ],
          specifications: {
            dimensions: '3m × 4m',
            layout: 'Round table',
            windows: 'No',
            accessibility: 'Wheelchair accessible'
          }
        },
        {
          id: 'room004',
          name: 'Executive Conference Room',
          description: 'Premium executive meeting space with state-of-the-art facilities',
          floor: 5,
          building: 'Main Building',
          capacity: 16,
          equipment: ['Smart Board', 'Video Conferencing', 'Sound System', 'WiFi', 'Dual Projectors'],
          amenities: ['Premium Furniture', 'City View', 'Coffee Station', 'Catering Setup'],
          hourly_rate: 50,
          availability: 'available',
          image: 'https://images.unsplash.com/photo-1582653291997-079a1c04e5a1?w=400',
          bookings: [
            { start: '11:00', end: '12:30', title: 'Executive Meeting', bookedBy: 'CEO Office' }
          ],
          specifications: {
            dimensions: '8m × 12m',
            layout: 'Executive boardroom',
            windows: 'Floor-to-ceiling',
            accessibility: 'Full accessibility'
          }
        },
        {
          id: 'room005',
          name: 'Training Center - Room 1',
          description: 'Large training room designed for workshops and educational sessions',
          floor: 2,
          building: 'Training Wing',
          capacity: 25,
          equipment: ['Projector', 'Audio System', 'Microphone', 'WiFi', 'Document Camera'],
          amenities: ['Theater Seating', 'Stage Area', 'Storage Space'],
          hourly_rate: 35,
          availability: 'maintenance',
          image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=400',
          bookings: [],
          specifications: {
            dimensions: '10m × 15m',
            layout: 'Theater style',
            windows: 'Yes',
            accessibility: 'Full accessibility'
          }
        },
        {
          id: 'room006',
          name: 'Consultation Room 5',
          description: 'Private consultation room for patient meetings and medical discussions',
          floor: 2,
          building: 'Medical Wing',
          capacity: 4,
          equipment: ['Computer', 'Medical Equipment Display', 'WiFi', 'Intercom'],
          amenities: ['Privacy Blinds', 'Comfortable Seating', 'Hand Sanitizer'],
          hourly_rate: 30,
          availability: 'available',
          image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400',
          bookings: [
            { start: '10:00', end: '11:00', title: 'Patient Consultation', bookedBy: 'Dr. Wilson' },
            { start: '15:00', end: '16:00', title: 'Family Meeting', bookedBy: 'Dr. Chen' }
          ],
          specifications: {
            dimensions: '3m × 4m',
            layout: 'Consultation setup',
            windows: 'Privacy glass',
            accessibility: 'Medical accessibility'
          }
        },
        {
          id: 'room007',
          name: 'Auditorium',
          description: 'Main auditorium for large presentations, conferences, and ceremonies',
          floor: 1,
          building: 'Main Building',
          capacity: 100,
          equipment: ['Stage', 'Audio/Visual System', 'Lighting', 'WiFi', 'Recording Equipment'],
          amenities: ['Theater Seating', 'Backstage Area', 'Green Room', 'Parking'],
          hourly_rate: 100,
          availability: 'available',
          image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=400',
          bookings: [
            { start: '19:00', end: '21:00', title: 'Medical Conference', bookedBy: 'Conference Committee' }
          ],
          specifications: {
            dimensions: '20m × 30m',
            layout: 'Auditorium style',
            windows: 'Controlled lighting',
            accessibility: 'Full accessibility'
          }
        }
      ];

      setRooms(mockRooms);
      setLoading(false);
    };

    setTimeout(fetchRooms, 500);
  }, []);

  const floors = ['all', '1', '2', '3', '4', '5'];
  const capacityRanges = [
    { id: 'all', name: 'All Capacities' },
    { id: '1-5', name: '1-5 people' },
    { id: '6-10', name: '6-10 people' },
    { id: '11-20', name: '11-20 people' },
    { id: '21+', name: '21+ people' }
  ];

  const equipmentTypes = [
    'all', 'Projector', 'Video Conferencing', 'Whiteboard', 'TV Display', 
    'Audio System', 'Smart Board', 'Conference Phone'
  ];

  const availabilityStatuses = [
    { id: 'all', name: 'All Rooms', color: 'gray' },
    { id: 'available', name: 'Available', color: 'green' },
    { id: 'busy', name: 'Busy', color: 'red' },
    { id: 'maintenance', name: 'Maintenance', color: 'yellow' }
  ];

  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         room.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         room.building.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFloor = selectedFloor === 'all' || room.floor.toString() === selectedFloor;

    const matchesCapacity = selectedCapacity === 'all' || (() => {
      switch (selectedCapacity) {
        case '1-5': return room.capacity >= 1 && room.capacity <= 5;
        case '6-10': return room.capacity >= 6 && room.capacity <= 10;
        case '11-20': return room.capacity >= 11 && room.capacity <= 20;
        case '21+': return room.capacity >= 21;
        default: return true;
      }
    })();

    const matchesEquipment = selectedEquipment === 'all' || 
                            room.equipment.includes(selectedEquipment);

    const matchesAvailability = selectedAvailability === 'all' || 
                               room.availability === selectedAvailability;

    return matchesSearch && matchesFloor && matchesCapacity && matchesEquipment && matchesAvailability;
  });

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'busy':
        return 'bg-red-100 text-red-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAvailabilityIcon = (availability) => {
    switch (availability) {
      case 'available':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'busy':
        return <XCircleIcon className="h-4 w-4" />;
      case 'maintenance':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <InformationCircleIcon className="h-4 w-4" />;
    }
  };

  const getEquipmentIcon = (equipment) => {
    switch (equipment) {
      case 'Projector':
        return <PresentationChartBarIcon className="h-4 w-4" />;
      case 'TV Display':
        return <TvIcon className="h-4 w-4" />;
      case 'Video Conferencing':
        return <ComputerDesktopIcon className="h-4 w-4" />;
      case 'Conference Phone':
        return <PhoneIcon className="h-4 w-4" />;
      case 'Audio System':
        return <SpeakerWaveIcon className="h-4 w-4" />;
      case 'WiFi':
        return <WifiIcon className="h-4 w-4" />;
      default:
        return <ComputerDesktopIcon className="h-4 w-4" />;
    }
  };

  const isRoomAvailableAtTime = (room, date, time, duration) => {
    if (room.availability !== 'available') return false;

    const requestedStart = new Date(`${date}T${time}`);
    const requestedEnd = new Date(requestedStart.getTime() + parseInt(duration) * 60000);

    return !room.bookings.some(booking => {
      const bookingStart = new Date(`${date}T${booking.start}`);
      const bookingEnd = new Date(`${date}T${booking.end}`);
      
      return (requestedStart < bookingEnd && requestedEnd > bookingStart);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center">
                <BuildingOfficeIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mr-3 flex-shrink-0" />
                <div className="min-w-0">
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Meeting Rooms</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    Book meeting rooms and conference spaces
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                <Link
                  to="/calendar/schedule"
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Book Room
                </Link>
                <Link
                  to="/calendar"
                  className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Calendar View
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search and Filters */}
        <div className="bg-white shadow rounded-lg sm:rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
            {/* Search */}
            <div className="flex-1 max-w-lg order-1 lg:order-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-9 sm:pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Search meeting rooms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Quick Booking */}
            <div className="order-2 lg:order-2">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:gap-2">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="border border-gray-300 rounded-md py-2 px-2 sm:px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="border border-gray-300 rounded-md py-2 px-2 sm:px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={bookingDuration}
                    onChange={(e) => setBookingDuration(e.target.value)}
                    className="flex-1 sm:flex-none border border-gray-300 rounded-md py-2 px-2 sm:px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="30">30 min</option>
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                    <option value="180">3 hours</option>
                  </select>

                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <FunnelIcon className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">Filters</span>
                    <span className="sm:hidden">Filter</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Floor
                  </label>
                  <select
                    value={selectedFloor}
                    onChange={(e) => setSelectedFloor(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Floors</option>
                    {floors.slice(1).map(floor => (
                      <option key={floor} value={floor}>Floor {floor}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacity
                  </label>
                  <select
                    value={selectedCapacity}
                    onChange={(e) => setSelectedCapacity(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {capacityRanges.map(range => (
                      <option key={range.id} value={range.id}>{range.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Equipment
                  </label>
                  <select
                    value={selectedEquipment}
                    onChange={(e) => setSelectedEquipment(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Equipment</option>
                    {equipmentTypes.slice(1).map(equipment => (
                      <option key={equipment} value={equipment}>{equipment}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Availability
                  </label>
                  <select
                    value={selectedAvailability}
                    onChange={(e) => setSelectedAvailability(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {availabilityStatuses.map(status => (
                      <option key={status.id} value={status.id}>{status.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setSelectedFloor('all');
                    setSelectedCapacity('all');
                    setSelectedEquipment('all');
                    setSelectedAvailability('all');
                    setSearchTerm('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Rooms Grid */}
        {filteredRooms.length === 0 ? (
          <div className="bg-white rounded-lg sm:rounded-xl shadow p-8 sm:p-12 text-center">
            <BuildingOfficeIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No rooms found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredRooms.map(room => (
              <div key={room.id} className="bg-white rounded-lg sm:rounded-xl shadow overflow-hidden hover:shadow-lg transition-shadow duration-200">
                {/* Room Image */}
                <div className="h-40 sm:h-48 bg-gray-200 relative">
                  <img
                    src={room.image}
                    alt={room.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 sm:top-3 right-2 sm:right-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getAvailabilityColor(room.availability)}`}>
                      {getAvailabilityIcon(room.availability)}
                      <span className="ml-1 capitalize">{room.availability}</span>
                    </span>
                  </div>
                </div>

                {/* Room Details */}
                <div className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex-1 min-w-0 pr-2">
                      {room.name}
                    </h3>
                    <div className="text-right flex-shrink-0">
                      <div className="text-base sm:text-lg font-bold text-blue-600">${room.hourly_rate}</div>
                      <div className="text-xs text-gray-500">per hour</div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {room.description}
                  </p>

                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <UserGroupIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{room.capacity} people</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <BuildingOfficeIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span className="truncate">Floor {room.floor}</span>
                    </div>
                  </div>

                  {/* Equipment */}
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Equipment:</div>
                    <div className="flex flex-wrap gap-1">
                      {room.equipment.slice(0, 4).map(equipment => (
                        <span
                          key={equipment}
                          className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                          title={equipment}
                        >
                          {getEquipmentIcon(equipment)}
                          <span className="ml-1 hidden sm:inline truncate">{equipment}</span>
                        </span>
                      ))}
                      {room.equipment.length > 4 && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                          +{room.equipment.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Availability Check */}
                  {selectedDate && selectedTime && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                        <div className="text-sm">
                          <span className="font-medium text-gray-700 block sm:inline">
                            {new Date(selectedDate).toLocaleDateString()}
                          </span>
                          <span className="text-gray-500 block sm:inline sm:ml-1">
                            at {selectedTime} ({bookingDuration} min)
                          </span>
                        </div>
                        {isRoomAvailableAtTime(room, selectedDate, selectedTime, bookingDuration) ? (
                          <span className="inline-flex items-center text-green-600 text-sm font-medium">
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            Available
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-red-600 text-sm font-medium">
                            <XCircleIcon className="h-4 w-4 mr-1" />
                            Booked
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Today's Schedule Preview */}
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Today's Schedule:</div>
                    {room.bookings.length > 0 ? (
                      <div className="space-y-1">
                        {room.bookings.slice(0, 2).map((booking, index) => (
                          <div key={index} className="text-xs text-gray-600 flex items-center">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            {booking.start}-{booking.end}: {booking.title}
                          </div>
                        ))}
                        {room.bookings.length > 2 && (
                          <div className="text-xs text-gray-500">
                            +{room.bookings.length - 2} more bookings
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">No bookings today</div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="flex items-center space-x-2">
                      <button
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                        title="View details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                        title="Check availability"
                      >
                        <CalendarIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="flex items-center justify-end">
                      {room.availability === 'available' ? (
                        <Link
                          to={`/calendar/schedule?room=${room.id}`}
                          className="inline-flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                        >
                          Book Now
                        </Link>
                      ) : (
                        <button
                          disabled
                          className="inline-flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded text-gray-400 bg-gray-100 cursor-not-allowed"
                        >
                          Unavailable
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <div className="bg-white rounded-lg shadow p-4 lg:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BuildingOfficeIcon className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600" />
              </div>
              <div className="ml-3 lg:ml-4">
                <p className="text-sm font-medium text-gray-500">Total Rooms</p>
                <p className="text-xl lg:text-2xl font-semibold text-gray-900">{rooms.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 lg:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircleIcon className="h-5 w-5 lg:h-6 lg:w-6 text-green-600" />
              </div>
              <div className="ml-3 lg:ml-4">
                <p className="text-sm font-medium text-gray-500">Available</p>
                <p className="text-xl lg:text-2xl font-semibold text-gray-900">
                  {rooms.filter(r => r.availability === 'available').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 lg:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircleIcon className="h-5 w-5 lg:h-6 lg:w-6 text-red-600" />
              </div>
              <div className="ml-3 lg:ml-4">
                <p className="text-sm font-medium text-gray-500">Busy</p>
                <p className="text-xl lg:text-2xl font-semibold text-gray-900">
                  {rooms.filter(r => r.availability === 'busy').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 lg:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <ArrowPathIcon className="h-5 w-5 lg:h-6 lg:w-6 text-yellow-600" />
              </div>
              <div className="ml-3 lg:ml-4">
                <p className="text-sm font-medium text-gray-500">Maintenance</p>
                <p className="text-xl lg:text-2xl font-semibold text-gray-900">
                  {rooms.filter(r => r.availability === 'maintenance').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingRooms;
